import React from "react";

export function ProfileAvatarRing({ user, size = 80, className = "" }) {
  // Calculate Profile Completion Percentage
  const calculateCompletion = () => {
    if (!user) return 0;

    const fields = [
      { key: "name", weight: 15 },
      { key: "email", weight: 20 },
      { key: "phone", weight: 25 }, // Required for anti-abuse validation
      { key: "company_name", weight: 15 },
      { key: "designation", weight: 10 },
      { key: "avatar_base64", weight: 15 }
    ];

    let completedWeight = 0;
    fields.forEach((field) => {
      const val = user[field.key];
      if (val && String(val).trim().length > 0) {
        completedWeight += field.weight;
      }
    });

    return completedWeight;
  };

  const percentage = calculateCompletion();

  const avatarUrl = user?.avatar_base64
    ? user.avatar_base64.startsWith("data:")
      ? user.avatar_base64
      : `data:image/jpeg;base64,${user.avatar_base64}`
    : null;

  // 🚀 IF PROFILE IS 100% COMPLETE: Render clean avatar without ring or badge
  if (percentage >= 100) {
    return (
      <div 
        className={`relative rounded-full overflow-hidden bg-slate-100 flex items-center justify-center font-black text-slate-700 border border-slate-200 shadow-sm shrink-0 ${className}`}
        style={{ width: size, height: size }}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt={user?.name || "User"} className="w-full h-full object-cover" />
        ) : (
          <span className="text-lg uppercase">
            {user?.name ? user.name.charAt(0) : "E"}
          </span>
        )}
      </div>
    );
  }

  // 🚀 IF INCOMPLETE (< 100%): Render progress ring and percentage badge
  const strokeWidth = 5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const getRingColor = () => {
    if (percentage < 50) return "#ef4444"; // Red
    if (percentage < 85) return "#f59e0b"; // Amber
    return "#10b981"; // Emerald
  };

  return (
    <div className={`relative inline-flex items-center justify-center shrink-0 ${className}`}>
      {/* SVG CIRCULAR PROGRESS RING */}
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Track Background */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e2e8f0"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Active Progress Ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={getRingColor()}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          fill="transparent"
          className="transition-all duration-700 ease-out"
        />
      </svg>

      {/* AVATAR IMAGE OR INITIAL */}
      <div 
        className="absolute rounded-full overflow-hidden bg-slate-100 flex items-center justify-center font-black text-slate-700 shadow-inner"
        style={{ width: size - 14, height: size - 14 }}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt={user?.name || "User"} className="w-full h-full object-cover" />
        ) : (
          <span className="text-lg uppercase">
            {user?.name ? user.name.charAt(0) : "E"}
          </span>
        )}
      </div>

      {/* PERCENTAGE BADGE */}
      <div 
        className="absolute -bottom-1 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase text-white shadow-md border border-white"
        style={{ backgroundColor: getRingColor() }}
      >
        {percentage}% Done
      </div>
    </div>
  );
}